import { useState, useEffect } from "react";
import PageTableHeader from "../../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../../components/table/table";
import DefaultButton from "../../../../components/buttons/defaultButton";
import DefaultDatePicker from "../../../../components/inputs/defaultDatePicker";
import DefaultSearchSelectInput from "../../../../components/inputs/defaultSearchSelectInput";
import { 
  generateStatutoryReport, 
  getStatutoryReports, 
  downloadStatutoryReport 
} from "../../../../../services/workspace/payrollManagement.service";
import { FiDownload, FiFileText, FiCalendar } from "react-icons/fi";
import toast from "react-hot-toast";
import moment from "moment";

const items = [{ label: "Statutory Reports", path: "" }];

const REPORT_TYPES = [
  { value: "PF", label: "Provident Fund (PF)" },
  { value: "ESI", label: "Employee State Insurance (ESI)" },
  { value: "PT", label: "Professional Tax (PT)" },
  { value: "24Q", label: "TDS 24Q" },
  { value: "LABOUR_WELFARE", label: "Labour Welfare Fund" },
  { value: "LWF", label: "LWF" },
];

const PERIOD_TYPES = [
  { value: "MONTH", label: "Month" },
  { value: "QUARTER", label: "Quarter" },
  { value: "YEAR", label: "Year" },
];

/** Normalize statutory-reports API (several envelope shapes) → { list, total } */
function parseStatutoryReportsResponse(response) {
  if (response == null) return { list: [], total: 0 };

  const unwrap =
    response?.data !== undefined && !Array.isArray(response)
      ? response.data
      : response;

  if (Array.isArray(unwrap)) {
    return { list: unwrap, total: unwrap.length };
  }
  if (unwrap && typeof unwrap === "object") {
    if (Array.isArray(unwrap.data)) {
      return {
        list: unwrap.data,
        total: unwrap.meta?.total ?? unwrap.data.length,
      };
    }
    if (unwrap.data && typeof unwrap.data === "object" && Array.isArray(unwrap.data.data)) {
      return {
        list: unwrap.data.data,
        total: unwrap.data.meta?.total ?? unwrap.data.data.length,
      };
    }
  }
  return { list: [], total: 0 };
}

const StatutoryReports = () => {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [selectedReportTypes, setSelectedReportTypes] = useState([]);
  const [periodType, setPeriodType] = useState("MONTH");
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [generating, setGenerating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [reportsTotal, setReportsTotal] = useState(0);

  useEffect(() => {
    fetchReports();
    // Set default period to current month
    const today = moment();
    const startOfMonth = today.clone().startOf("month");
    const endOfMonth = today.clone().endOf("month");
    setPeriodStart(startOfMonth.format("YYYY-MM-DD"));
    setPeriodEnd(endOfMonth.format("YYYY-MM-DD"));
    setSelectedMonth(today.format("YYYY-MM"));
  }, []);

  useEffect(() => {
    updatePeriodDates();
  }, [periodType, selectedMonth, selectedQuarter, selectedYear]);

  const updatePeriodDates = () => {
    let start, end;
    const today = moment();

    if (periodType === "MONTH" && selectedMonth) {
      const monthMoment = moment(selectedMonth, "YYYY-MM");
      start = monthMoment.clone().startOf("month");
      end = monthMoment.clone().endOf("month");
    } else if (periodType === "QUARTER" && selectedQuarter) {
      const [year, quarter] = selectedQuarter.split("-Q");
      const quarterMoment = moment().year(parseInt(year)).quarter(parseInt(quarter));
      start = quarterMoment.clone().startOf("quarter");
      end = quarterMoment.clone().endOf("quarter");
    } else if (periodType === "YEAR" && selectedYear) {
      start = moment(`${selectedYear}-01-01`);
      end = moment(`${selectedYear}-12-31`);
    } else {
      return;
    }

    setPeriodStart(start.format("YYYY-MM-DD"));
    setPeriodEnd(end.format("YYYY-MM-DD"));
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      const response = await getStatutoryReports({
        page: currentPage,
        limit: 10,
        period_start: periodStart,
        period_end: periodEnd,
        period_type: periodType,
      });

      const { list, total } = parseStatutoryReportsResponse(response);
      setReports(Array.isArray(list) ? list : []);
      setReportsTotal(typeof total === "number" ? total : 0);
    } catch (error) {
      console.error("Reports fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to fetch statutory reports");
      setReports([]);
      setReportsTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (selectedReportTypes.length === 0) {
      toast.error("Please select at least one report type");
      return;
    }
    if (!periodStart || !periodEnd) {
      toast.error("Please select a valid period");
      return;
    }

    try {
      setGenerating(true);
      toast.loading("Generating reports...");
      
      const response = await generateStatutoryReport({
        report_types: selectedReportTypes,
        period_start: periodStart,
        period_end: periodEnd,
        period_type: periodType,
      });
      
      if (response) {
        toast.dismiss();
        toast.success("Reports generated successfully!");
        fetchReports();
      }
    } catch (error) {
      console.error("Generate reports error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to generate reports");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (report) => {
    const uuid = report?.uuid ?? report?.id;
    if (!uuid) {
      toast.error("Missing report identifier");
      return;
    }
    try {
      toast.loading("Downloading report...");

      const res = await downloadStatutoryReport(uuid);

      // Backend may return either:
      // 1) actual Blob (axios responseType: 'blob')
      // 2) a JSON envelope with `download_url`
      const directUrl =
        res?.data?.download_url || res?.download_url || res?.data?.url || res?.url;

      if (typeof directUrl === "string" && directUrl.length > 0) {
        window.open(directUrl, "_blank", "noopener,noreferrer");
        toast.dismiss();
        toast.success("Report downloaded successfully!");
        return;
      }

      const isBlob = typeof Blob !== "undefined" && res instanceof Blob;

      if (isBlob) {
        // If backend returned JSON but we requested blob, parse it to extract download_url.
        const isLikelyJson =
          typeof res.type === "string" &&
          res.type.toLowerCase().includes("json");

        if (isLikelyJson || res.size < 1024 * 50) {
          try {
            const text = await res.text();
            const parsed = JSON.parse(text);
            const urlFromJson =
              parsed?.data?.download_url ||
              parsed?.download_url ||
              parsed?.data?.url ||
              parsed?.url;
            if (typeof urlFromJson === "string" && urlFromJson.length > 0) {
              window.open(urlFromJson, "_blank", "noopener,noreferrer");
              toast.dismiss();
              toast.success("Report downloaded successfully!");
              return;
            }
          } catch (e) {
            // Not JSON; proceed with normal blob download.
          }
        }

        // Normal blob download (real PDF/CSV).
        const type = report?.report_type || "";
        const ext = type === "PF" || type === "ESI" ? "csv" : "pdf";

        const url = window.URL.createObjectURL(res);
        const link = document.createElement("a");
        link.href = url;
        link.download = `statutory_${type || "report"}_${uuid}.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.dismiss();
        toast.success("Report downloaded successfully!");
        return;
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.dismiss();
      toast.error(error?.response?.data?.message || "Failed to download report");
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getReportTypeLabel = (type) => {
    const report = REPORT_TYPES.find((r) => r.value === type);
    return report?.label || type;
  };

  const getStatusBadge = (status) => {
    const key = status ? String(status).toUpperCase().trim() : "";

    if (key === "COMPLETED") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Completed
        </span>
      );
    }
    if (key === "GENERATED") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
          Generated
        </span>
      );
    }
    if (key === "FAILED" || key === "ERROR") {
      return (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
          Failed
        </span>
      );
    }
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        Processing
      </span>
    );
  };

  const reportsList = Array.isArray(reports) ? reports : [];
  const reportsTableData = reportsList.map((report) => ({
    id: report.id,
    report_type: {
      data: getReportTypeLabel(report.report_type),
      variant: "text",
    },
    period: {
      data: `${formatDate(report.period_start)} - ${formatDate(report.period_end)}`,
      variant: "text",
    },
    status: {
      data: getStatusBadge(report.status),
      variant: "text",
    },
    generated_at: {
      data: formatDateTime(report.generated_at),
      variant: "text",
    },
    generated_by: {
      data:
        report.created_by?.name ||
        (typeof report.generated_by === "string" ? report.generated_by : null) ||
        "-",
      variant: "text",
    },
    actions: {
      data: (
        <button
          type="button"
          onClick={() => handleDownload(report)}
          className="text-theme hover:text-themeHover"
          title="Download"
        >
          <FiDownload className="w-4 h-4" />
        </button>
      ),
      variant: "text",
    },
  }));

  const reportsTableHeaders = [
    { label: "Report Type", key: "report_type" },
    { label: "Period", key: "period" },
    { label: "Status", key: "status" },
    { label: "Generated At", key: "generated_at" },
    { label: "Generated By", key: "generated_by" },
    { label: "Actions", key: "actions" },
  ];

  const generateQuarterOptions = () => {
    const currentYear = moment().year();
    const options = [];
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      for (let quarter = 1; quarter <= 4; quarter++) {
        options.push({
          value: `${year}-Q${quarter}`,
          label: `Q${quarter} ${year}`,
        });
      }
    }
    return options;
  };

  const generateYearOptions = () => {
    const currentYear = moment().year();
    const options = [];
    for (let year = currentYear - 2; year <= currentYear + 1; year++) {
      options.push({
        value: year.toString(),
        label: year.toString(),
      });
    }
    return options;
  };

  return (
    <div className="flex flex-col gap-4">
      <PageTableHeader
        isSearch={false}
        filter={false}
        shorting={false}
        breadcrumbs={items}
      />

      {/* Generate Report Form */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Generate Statutory Reports</h3>
        
        <div className="space-y-4">
          {/* Report Types */}
          <div>
            <label className="block text-sm font-medium text-theme mb-2">
              Select Report Types
            </label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {REPORT_TYPES.map((type) => (
                <label
                  key={type.value}
                  className="flex items-center gap-2 p-3 border border-borderColor rounded-lg hover:bg-primary-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedReportTypes.includes(type.value)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedReportTypes([...selectedReportTypes, type.value]);
                      } else {
                        setSelectedReportTypes(selectedReportTypes.filter((t) => t !== type.value));
                      }
                    }}
                    className="w-4 h-4 text-theme border-borderColor rounded focus:ring-theme"
                  />
                  <span className="text-sm text-theme">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Period Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-theme mb-2">
                Period Type
              </label>
              <DefaultSearchSelectInput
                options={PERIOD_TYPES}
                value={PERIOD_TYPES.find((p) => p.value === periodType)}
                onChange={(option) => setPeriodType(option?.value || "MONTH")}
                placeholder="Select period type"
              />
            </div>

            {periodType === "MONTH" && (
              <div>
                <label className="block text-sm font-medium text-theme mb-2">
                  Select Month
                </label>
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-4 py-2 border border-borderColor rounded-lg focus:outline-none focus:ring-2 focus:ring-theme"
                />
              </div>
            )}

            {periodType === "QUARTER" && (
              <div>
                <label className="block text-sm font-medium text-theme mb-2">
                  Select Quarter
                </label>
                <DefaultSearchSelectInput
                  options={generateQuarterOptions()}
                  value={generateQuarterOptions().find((q) => q.value === selectedQuarter)}
                  onChange={(option) => setSelectedQuarter(option?.value || "")}
                  placeholder="Select quarter"
                />
              </div>
            )}

            {periodType === "YEAR" && (
              <div>
                <label className="block text-sm font-medium text-theme mb-2">
                  Select Year
                </label>
                <DefaultSearchSelectInput
                  options={generateYearOptions()}
                  value={generateYearOptions().find((y) => y.value === selectedYear)}
                  onChange={(option) => setSelectedYear(option?.value || "")}
                  placeholder="Select year"
                />
              </div>
            )}
          </div>

          {/* Period Display */}
          {periodStart && periodEnd && (
            <div className="bg-primary-50 p-3 rounded-lg border border-borderColor">
              <div className="flex items-center gap-2">
                <FiCalendar className="w-4 h-4 text-theme" />
                <span className="text-sm text-theme">
                  <strong>Period:</strong> {formatDate(periodStart)} to {formatDate(periodEnd)}
                </span>
              </div>
            </div>
          )}

          {/* Generate Button */}
          <div className="flex justify-end">
            <DefaultButton
              name="Generate Reports"
              icon={<FiFileText className="w-4 h-4" />}
              onClick={handleGenerate}
              disabled={generating || selectedReportTypes.length === 0 || !periodStart || !periodEnd}
            />
          </div>
        </div>
      </div>

      {/* Generated Reports List */}
      <div className="bg-white p-4 rounded-lg border border-borderColor">
        <h3 className="text-lg font-semibold text-theme mb-4">Generated Reports</h3>
        {loading ? (
          <div className="text-center py-8 text-theme">Loading reports...</div>
        ) : reportsList.length === 0 ? (
          <div className="text-center py-8 text-textLight">No reports generated yet</div>
        ) : (
          <CommonTable
            TableHeaderData={reportsTableHeaders}
            tabledata={reportsTableData}
            currentPage={currentPage}
            totalData={reportsTotal}
            setCurrentPage={setCurrentPage}
            loading={false}
            action={{}}
          />
        )}
      </div>
    </div>
  );
};

export default StatutoryReports;

